import { useState, useEffect } from "react"
import { useSearchParams, useNavigate, Routes, Route } from "react-router-dom"
import { Layout, Menu, Button } from "antd"
import { DashboardOutlined, FileTextOutlined, MenuFoldOutlined, MenuUnfoldOutlined, ClockCircleOutlined } from "@ant-design/icons"
import TimesheetDashboard from "./TimesheetDashboard"
import TimesheetList from "./TimesheetList"
import TimesheetForm from "./TimesheetForm"

const { Sider, Content, Header } = Layout

const TimesheetMain = () => {
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();
    const [selectedKey, setSelectedKey] = useState('dashboard');
    const [collapsed, setCollapsed] = useState(false);
    const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
    const accessToken = searchParams.get('token') || localStorage.getItem('timesheet_access_token');

    useEffect(() => {
        const handleResize = () => {
            const mobile = window.innerWidth < 768;
            setIsMobile(mobile);
            if (mobile) {
                setCollapsed(true);
            }
        };

        window.addEventListener('resize', handleResize);
        handleResize(); // Initial check

        return () => window.removeEventListener('resize', handleResize);
    }, []);

    useEffect(() => {
        // Store token in localStorage if present in URL
        const token = searchParams.get('token');
        if (token) {
            localStorage.setItem('timesheet_access_token', token);
        }
    }, [searchParams]);

    // Redirect to dashboard if on root timesheet path
    useEffect(() => {
        const path = window.location.pathname;
        if (path === '/timesheet' || path === '/timesheet/') {
            const token = accessToken || localStorage.getItem('timesheet_access_token');
            if (token) {
                navigate(`/timesheet/dashboard${token ? `?token=${token}` : ''}`);
            }
        }
    }, [accessToken, navigate]);

    const menuItems = [
        {
            key: 'dashboard',
            icon: <DashboardOutlined />,
            label: 'Dashboard',
            path: '/timesheet/dashboard'
        },
        {
            key: 'list',
            icon: <FileTextOutlined />,
            label: 'Timesheet List',
            path: '/timesheet/list'
        }
    ];

    const handleMenuClick = (key: string) => {
        setSelectedKey(key);
        const item = menuItems.find(i => i.key === key);
        if (item) {
            const token = accessToken || localStorage.getItem('timesheet_access_token');
            navigate(`${item.path}${token ? `?token=${token}` : ''}`);
            // Close sidebar on mobile after navigation
            if (isMobile) {
                setCollapsed(true);
            }
        }
    };

    // Set initial selected key based on current path
    useEffect(() => {
        const path = window.location.pathname;
        if (path.includes('/list')) {
            setSelectedKey('list');
        } else {
            setSelectedKey('dashboard');
        }
    }, []);

    if (!accessToken) {
        // If no token, show only the form (for initial login)
        return <TimesheetForm />;
    }

    return (
        <Layout style={{ minHeight: '100vh', background: '#f5f7fa' }}>
            {!isMobile && (
                <Sider
                    width={240}
                    style={{
                        background: 'linear-gradient(180deg, #ffffff 0%, #fafbfc 100%)',
                        boxShadow: '2px 0 12px rgba(0,0,0,0.08)',
                        borderRight: '1px solid #e8eaed'
                    }}
                >
                    <div style={{
                        padding: '24px 20px',
                        textAlign: 'center',
                        borderBottom: '1px solid #e8eaed',
                        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                        marginBottom: '8px'
                    }}>
                        <h2 style={{
                            margin: 0,
                            color: '#fff',
                            fontSize: '20px',
                            fontWeight: 600,
                            letterSpacing: '0.5px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            gap: '8px'
                        }}>
                            <ClockCircleOutlined /> Timesheet
                        </h2>
                    </div>
                    <Menu
                        mode="inline"
                        selectedKeys={[selectedKey]}
                        onClick={({ key }) => handleMenuClick(key)}
                        style={{
                            borderRight: 0,
                            background: 'transparent',
                            padding: '8px'
                        }}
                        theme="light"
                    >
                        {menuItems.map(item => (
                            <Menu.Item
                                key={item.key}
                                icon={item.icon}
                                style={{
                                    borderRadius: '8px',
                                    margin: '4px 0',
                                    height: '48px',
                                    lineHeight: '48px',
                                    fontSize: '15px',
                                    fontWeight: selectedKey === item.key ? 500 : 400
                                }}
                            >
                                {item.label}
                            </Menu.Item>
                        ))}
                    </Menu>
                </Sider>
            )}
            <Layout>
                {isMobile && (
                    <Header style={{
                        background: '#fff',
                        padding: '0 16px',
                        boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
                        position: 'sticky',
                        top: 0,
                        zIndex: 999,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        height: '64px',
                        lineHeight: '64px'
                    }}>
                        <Menu
                            mode="horizontal"
                            selectedKeys={[selectedKey]}
                            onClick={({ key }) => handleMenuClick(key)}
                            style={{
                                border: 'none',
                                background: 'transparent',
                                minWidth: 'auto',
                                width: '100%',
                                display: 'flex',
                                justifyContent: 'center'
                            }}
                            theme="light"
                        >
                            {menuItems.map(item => (
                                <Menu.Item
                                    key={item.key}
                                    icon={item.icon}
                                    style={{ fontSize: '13px' }}
                                >
                                    {item.label}
                                </Menu.Item>
                            ))}
                        </Menu>
                    </Header>
                )}
                <Content style={{ margin: '0', background: '#f5f7fa', minHeight: '100vh' }}>
                    <Routes>
                        <Route path="/dashboard" element={<TimesheetDashboard />} />
                        <Route path="/list" element={<TimesheetList />} />
                        <Route path="/form" element={<TimesheetForm />} />
                        <Route path="/" element={<TimesheetDashboard />} />
                    </Routes>
                </Content>
            </Layout>
        </Layout>
    );
};

export default TimesheetMain;
